/**
 * useOnboardingPicker Hook
 *
 * Manages onboarding picker state including folder navigation,
 * search, and selection with automatic latest version fetching.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { OnboardingFolderService } from '@modules/firebase-core/services/OnboardingFolderService';
import type { OnboardingListItem } from '@modules/onboardings/types/onboardings.types';
import type {
  UseOnboardingPickerResult,
  OnboardingSelection,
  BreadcrumbItem,
} from './OnboardingPicker.types';

export interface UseOnboardingPickerOptions {
  /** Project ID for loading onboardings */
  projectId: string;
}

/**
 * Hook for managing onboarding picker state
 *
 * @param options - Configuration options
 * @returns Picker state and actions
 *
 * @example
 * ```typescript
 * const {
 *   items,
 *   loading,
 *   navigateToFolder,
 *   selectOnboarding,
 * } = useOnboardingPicker({ projectId: 'project-123' });
 * ```
 */
export function useOnboardingPicker(
  options: UseOnboardingPickerOptions
): UseOnboardingPickerResult {
  const { projectId } = options;

  // State
  const [allItems, setAllItems] = useState<OnboardingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentFolderName, setCurrentFolderName] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [searchQuery, setSearchQueryState] = useState('');

  // Services (created once)
  const onboardingServiceRef = useRef(new OnboardingService());
  const folderServiceRef = useRef(new OnboardingFolderService());

  /**
   * Load data for current folder
   */
  const loadData = useCallback(async (folderId: string | null) => {
    setLoading(true);
    setError(null);

    try {
      const onboardingService = onboardingServiceRef.current;
      const folderService = folderServiceRef.current;

      // Load folders (only at root level)
      const folders = folderId === null
        ? await folderService.list(projectId)
        : [];

      // Load onboardings based on current folder
      const onboardings = folderId === null
        ? await onboardingService.listRootOnboardings(projectId)
        : await onboardingService.listByFolder(projectId, folderId);

      // Transform to OnboardingListItem with type
      const folderItems: OnboardingListItem[] = folders.map(folder => ({
        ...folder,
        type: 'folder' as const,
      }));

      const onboardingItems: OnboardingListItem[] = onboardings.map(onboarding => ({
        ...onboarding,
        type: 'onboarding' as const,
      }));

      // Combine: folders first, then onboardings
      const combinedItems = [...folderItems, ...onboardingItems];
      setAllItems(combinedItems);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load onboardings';
      setError(message);
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  /**
   * Filter items by search query
   */
  const items = useMemo(() => {
    if (!searchQuery.trim()) {
      return allItems;
    }

    const query = searchQuery.toLowerCase();
    return allItems.filter(item =>
      item.name.toLowerCase().includes(query)
    );
  }, [allItems, searchQuery]);

  /**
   * Navigate into a folder
   */
  const navigateToFolder = useCallback((folderId: string, folderName: string) => {
    // Clear search when navigating
    setSearchQueryState('');

    // Update breadcrumb
    if (breadcrumb.length === 0) {
      // First navigation - add Root and the folder
      setBreadcrumb([
        { id: null, name: 'Root' },
        { id: folderId, name: folderName },
      ]);
    } else {
      // Add to existing breadcrumb
      setBreadcrumb(prev => [...prev, { id: folderId, name: folderName }]);
    }

    setCurrentFolderId(folderId);
    setCurrentFolderName(folderName);
    loadData(folderId);
  }, [breadcrumb.length, loadData]);

  /**
   * Navigate to root
   */
  const navigateToRoot = useCallback(() => {
    setSearchQueryState('');
    setBreadcrumb([]);
    setCurrentFolderId(null);
    setCurrentFolderName(null);
    loadData(null);
  }, [loadData]);

  /**
   * Navigate to a breadcrumb item
   */
  const navigateToBreadcrumb = useCallback((index: number) => {
    const item = breadcrumb[index];
    if (!item) return;

    setSearchQueryState('');

    if (item.id === null) {
      // Root
      setBreadcrumb([]);
      setCurrentFolderId(null);
      setCurrentFolderName(null);
    } else {
      // Truncate breadcrumb to this point
      setBreadcrumb(prev => prev.slice(0, index + 1));
      setCurrentFolderId(item.id);
      setCurrentFolderName(item.name);
    }

    loadData(item.id);
  }, [breadcrumb, loadData]);

  /**
   * Set search query
   */
  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
  }, []);

  /**
   * Select an onboarding and get full data with version
   */
  const selectOnboarding = useCallback(async (
    onboardingId: string
  ): Promise<OnboardingSelection | null> => {
    try {
      const onboardingService = onboardingServiceRef.current;

      // Get onboarding details
      const onboarding = await onboardingService.getOnboarding(projectId, onboardingId);
      if (!onboarding) {
        return null;
      }

      // Get latest version
      const version = await onboardingService.getLatestVersion(projectId, onboardingId);
      if (!version) {
        return null;
      }

      return {
        onboardingId: onboarding.id,
        onboardingName: onboarding.name,
        versionId: version.id,
        versionName: version.name,
        versionCreatedAt: version.createdAt,
      };
    } catch (err) {
      console.error('Failed to select onboarding:', err);
      return null;
    }
  }, [projectId]);

  /**
   * Refresh the list
   */
  const refresh = useCallback(async () => {
    await loadData(currentFolderId);
  }, [loadData, currentFolderId]);

  // Load data on mount
  useEffect(() => {
    loadData(null);
  }, [loadData]);

  return {
    items,
    loading,
    error,
    currentFolderId,
    currentFolderName,
    breadcrumb,
    searchQuery,
    navigateToFolder,
    navigateToRoot,
    navigateToBreadcrumb,
    setSearchQuery,
    selectOnboarding,
    refresh,
  };
}
